import React from 'react';
import * as XLSX from 'xlsx';

const JunctionBoxSealantWeightVerification = () => {
  const inspectionData = JSON.parse(sessionStorage.getItem('inspectionData') || '{}');
  const sealantData = inspectionData.JunctionBoxSealantWeightVerification;

  if (!sealantData || !sealantData.lines) {
    return <p className="text-gray-500">No Junction Box Sealant Weight Verification data available</p>;
  }

  // Function to download data as Excel
  const downloadExcel = () => {
    const workbook = XLSX.utils.book_new();
    const worksheetData = [];

    // Headers for the Excel sheet
    worksheetData.push([
      'Line',
      'Date',
      'Shift',
      'Glue Weight - Left',
      'Glue Weight - Middle',
      'Glue Weight - Right',
      'Initial JB Weight - Left',
      'Initial JB Weight - Middle',
      'Initial JB Weight - Right',
      'JB With Glue Weight - Left',
      'JB With Glue Weight - Middle',
      'JB With Glue Weight - Right',
      'Sealant Uniformity',
      'Remark',
    ]);

    // Populate data for each line
    Object.entries(sealantData.lines).forEach(([lineKey, lineData]) => {
      worksheetData.push([
        lineKey,
        sealantData.date,
        sealantData.shift,
        lineData.glueWeight.left,
        lineData.glueWeight.middle,
        lineData.glueWeight.right,
        lineData.initialJBWeight.left,
        lineData.initialJBWeight.middle,
        lineData.initialJBWeight.right,
        lineData.jbWithGlueWeight.left,
        lineData.jbWithGlueWeight.middle,
        lineData.jbWithGlueWeight.right,
        lineData.sealantUniformity,
        lineData.remark,
      ]);
    });

    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    XLSX.utils.book_append_sheet(workbook, worksheet, 'JunctionBoxSealant');
    XLSX.writeFile(workbook, 'JunctionBoxSealantWeightVerificationData.xlsx');
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-semibold text-gray-800">
          Junction Box Sealant Weight Verification - {sealantData.date} ({sealantData.shift})
        </h2>
        <button
          onClick={downloadExcel}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none"
        >
          Download Excel
        </button>
      </div>

      {Object.entries(sealantData.lines).map(([lineKey, lineData]) => (
        <div key={lineKey} className="border-b pb-4">
          <h3 className="text-xl font-semibold text-gray-800 mb-4">{lineKey}</h3>

          {/* Glue Weight */}
          <div className="mt-4">
            <h4 className="font-semibold text-gray-700 mb-2">Glue Weight</h4>
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white border rounded-lg">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="px-4 py-2 text-left text-gray-700">Left</th>
                    <th className="px-4 py-2 text-left text-gray-700">Middle</th>
                    <th className="px-4 py-2 text-left text-gray-700">Right</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t">
                    <td className="px-4 py-2 text-gray-600">{lineData.glueWeight.left ?? 'N/A'}</td>
                    <td className="px-4 py-2 text-gray-600">{lineData.glueWeight.middle ?? 'N/A'}</td>
                    <td className="px-4 py-2 text-gray-600">{lineData.glueWeight.right ?? 'N/A'}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Initial JB Weight */}
          <div className="mt-4">
            <h4 className="font-semibold text-gray-700 mb-2">Initial JB Weight</h4>
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white border rounded-lg">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="px-4 py-2 text-left text-gray-700">Left</th>
                    <th className="px-4 py-2 text-left text-gray-700">Middle</th>
                    <th className="px-4 py-2 text-left text-gray-700">Right</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t">
                    <td className="px-4 py-2 text-gray-600">{lineData.initialJBWeight.left ?? 'N/A'}</td>
                    <td className="px-4 py-2 text-gray-600">{lineData.initialJBWeight.middle ?? 'N/A'}</td>
                    <td className="px-4 py-2 text-gray-600">{lineData.initialJBWeight.right ?? 'N/A'}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* JB With Glue Weight */}
          <div className="mt-4">
            <h4 className="font-semibold text-gray-700 mb-2">JB With Glue Weight</h4>
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white border rounded-lg">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="px-4 py-2 text-left text-gray-700">Left</th>
                    <th className="px-4 py-2 text-left text-gray-700">Middle</th>
                    <th className="px-4 py-2 text-left text-gray-700">Right</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t">
                    <td className="px-4 py-2 text-gray-600">{lineData.jbWithGlueWeight.left ?? 'N/A'}</td>
                    <td className="px-4 py-2 text-gray-600">{lineData.jbWithGlueWeight.middle ?? 'N/A'}</td>
                    <td className="px-4 py-2 text-gray-600">{lineData.jbWithGlueWeight.right ?? 'N/A'}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Scalar Fields */}
          <div className="mt-4">
            <h4 className="font-semibold text-gray-700 mb-2">Additional Details</h4>
            <div className="grid grid-cols-2 gap-4">
              <p><strong>Sealant Uniformity:</strong> {lineData.sealantUniformity || 'N/A'}</p>
              <p><strong>Remark:</strong> {lineData.remark || 'N/A'}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default JunctionBoxSealantWeightVerification;